/**
 * Pure trigger evaluator. Given state + already-unlocked set + catalog,
 * returns the artifacts that just crossed their trigger this turn.
 *
 * No side effects, no IO. The store calls this after every tap/
 * crystallize/upgrade/lesson-unlock/activation-dismiss and queues the
 * results for the reveal overlay.
 */
import type { ArtifactEvalState, ArtifactTrigger, VisualArtifact } from './types.js';

/** Returns true iff the given trigger's condition is currently satisfied. */
export function matchTrigger(state: ArtifactEvalState, trigger: ArtifactTrigger): boolean {
  switch (trigger.kind) {
    case 'crystallizationsAtLeast':
      return state.crystallizations >= trigger.value;
    case 'crystallizationsExactly':
      return state.crystallizations === trigger.value;
    case 'totalTapsAtLeast':
      return state.totalTaps >= trigger.value;
    case 'spiritBallsAtLeast':
      return state.spiritBalls >= trigger.value;
    case 'rhythmStreakReached':
      return state.rhythmBest >= trigger.value;
    case 'lessonsUnlockedAtLeast':
      return state.lessonsUnlocked >= trigger.value;
    case 'phrasesCollectedAtLeast':
      return state.phrasesCollected >= trigger.value;
    case 'daysPlayedAtLeast':
      return state.daysPlayed >= trigger.value;
    case 'activationsSeenAtLeast':
      return state.activationsSeen >= trigger.value;
    case 'goldenSparksCaughtAtLeast':
      return state.goldenSparksCaught >= trigger.value;
    case 'profileNameSet':
      return state.hasProfileName;
    case 'epochReached':
      return state.epoch >= trigger.epoch;
    case 'firstTapInTimeWindow':
      return state.hourOfDayLocal >= trigger.fromHour && state.hourOfDayLocal < trigger.toHour;
    case 'upgradePurchasedExactly': {
      // 2026-05-26: kind name historic — на самом деле проверяет
      // level >= trigger.level (artifact unlock'ается при достижении
      // уровня и остаётся unlocked на всех высших уровнях). Удержано
      // как `Exactly` для backward compat persisted unlocked Set,
      // semantically это «atLeast». Каждый artifact упоминает уровень
      // на котором он впервые проявился (обычно 1).
      const level = state.upgradeLevels.get(trigger.upgradeId) ?? 0;
      return level >= trigger.level;
    }
    case 'totalUpgradesAtLeast':
      return state.totalUpgrades >= trigger.value;
  }
}

/**
 * Evaluate every artifact in the catalog. Returns those whose trigger
 * is satisfied now AND that aren't already unlocked.
 *
 * Order of the returned array preserves catalog order — useful so the
 * reveal overlay shows lower-priority small things before headline
 * milestones from the same tap.
 */
export function evaluateArtifacts(opts: {
  readonly state: ArtifactEvalState;
  readonly alreadyUnlocked: ReadonlySet<string>;
  readonly catalog: readonly VisualArtifact[];
}): readonly VisualArtifact[] {
  const out: VisualArtifact[] = [];
  for (const artifact of opts.catalog) {
    if (opts.alreadyUnlocked.has(artifact.id)) continue;
    if (matchTrigger(opts.state, artifact.trigger)) {
      out.push(artifact);
    }
  }
  return out;
}
